/**
 * Gallery scene: open-in-tab echo on sidebar trees + Open Tabs pin echo
 * (?test=maater-ui#open-echo)
 */
import type { CSSProperties } from 'react'
import { FileText, Pin } from 'lucide-react'
import { ModePanelNavItem } from '@/components/layout/sidebar/ModePanelNavItem'
import { SidebarTreeRow } from '@/components/layout/sidebar/SidebarTreeRow'
import { wsNavSectionStyle } from '@/lib/ws-nav'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

const WS_COLOR = '#c47a2c'

export function OpenEchoScene() {
  return (
    <div className="space-y-6" data-testid={testIds.maaterUiGallery.openEchoScene}>
      <p className="text-sm" style={{ color: 'var(--ink)' }}>
        <strong className="font-medium">Open echo</strong> marks rows that are open in a tab
        but not focused: light workspace wash + trailing color dot (
        <code className="text-xs">ws-nav-item--open</code>
        ). Focused leaf keeps fill + left bar. Open Tabs rows skip the trailing dot (every
        row is open) and echo pin / italic preview like the tab bar. Right-click Open Tabs
        uses <code className="text-xs">TabContextMenuHost</code> (same menu as horizontal
        tabs - ADR-0020). Match helper: <code className="text-xs">@/lib/open-tab-match</code>.
      </p>

      <div
        className="ws-nav-section max-w-sm rounded-md border border-border p-2"
        style={wsNavSectionStyle(WS_COLOR)}
        data-testid={testIds.maaterUiGallery.openEchoTree}
      >
        <p className="mb-1 px-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Browse tree
        </p>
        <SidebarTreeRow
          padLeft={12}
          icon={<FileText className="h-3.5 w-3.5" />}
          label="Default (closed)"
        />
        <SidebarTreeRow
          padLeft={12}
          open
          icon={<FileText className="h-3.5 w-3.5" />}
          label="Open elsewhere (dot + wash)"
        />
        <SidebarTreeRow
          padLeft={12}
          active
          icon={<FileText className="h-3.5 w-3.5" />}
          label="Focused (active)"
        />
        <ModePanelNavItem
          dense
          open
          icon={<FileText className="h-3.5 w-3.5 text-muted-foreground" />}
          label="Extension noun open"
        />
      </div>

      <div
        className="ws-tab-group max-w-sm rounded-md border border-border p-2"
        style={{ '--ws-color': WS_COLOR } as CSSProperties}
        data-testid={testIds.maaterUiGallery.openEchoOpenTabs}
      >
        <p className="mb-1 px-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Open Tabs rows
        </p>
        <div
          className={cn(
            'ws-nav-item ws-nav-item--open group flex items-center gap-2 px-2 py-1 text-sm',
          )}
        >
          <Pin className="h-2.5 w-2.5 flex-none text-muted-foreground" aria-label="Pinned" />
          <FileText className="h-3.5 w-3.5 flex-none" />
          <span className="truncate text-[13px]">Pinned tab (pin echo)</span>
        </div>
        <div
          className={cn(
            'ws-nav-item ws-nav-item--active group flex items-center gap-2 px-2 py-1 text-sm text-sidebar-foreground',
          )}
        >
          <FileText className="h-3.5 w-3.5 flex-none" />
          <span className="truncate text-[13px] italic text-muted-foreground/80">
            Preview tab (italic, unpinned)
          </span>
        </div>
        <p className="mt-2 px-2 text-[11px] text-muted-foreground">
          Right-click production Open Tabs rows for Pin / Unpin / Close (same as tab bar).
        </p>
      </div>
    </div>
  )
}
